import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';

export interface RegisterRequest {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
}

export interface RegisterResponse {
  id?: number;
  firstName?: string;
  lastName?: string;
  email?: string;
  phoneNumber?: string;
  createdAt?: string;
  message: string;
  /** True if the verification email was sent; false if delivery failed. */
  emailSent?: boolean;
}

export interface LoginRequest {
  email: string;
  password: string;
}

export interface LoginResponse {
  success: boolean;
  message: string;
  /** True when login was rejected because the account is deactivated. */
  isDeactivated?: boolean;
  accessToken?: string;
  refreshToken?: string;
  tokenType?: string;
  expiresIn?: number;
}

export interface UserProfileResponse {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  language?: string;
  companyName: string;
  profession?: string;
  /** "product" | "service" when set */
  workType?: string | null;
  gstNumber?: string;
  panNumber?: string;
  address: string;
  companyLogoPath?: string;
  signaturePath?: string;
  signatureBase64?: string;
  currency: string;
  termsAndConditions?: string[];
  showTermsAndConditions: boolean;
  createdAt: string;
  /** Template names visible to this user (e.g. Classic, Executive, Professional). Empty = all visible. */
  visibleTemplates?: string[];
}

export interface RefreshTokenRequest {
  refreshToken: string;
}

export interface RefreshTokenResponse {
  success: boolean;
  message: string;
  accessToken?: string;
  refreshToken?: string;
}

export interface UpdateUserRequest {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber?: string;
  language?: string;
  companyName: string;
  profession?: string;
  workType?: string | null;
  gstNumber?: string;
  panNumber?: string;
  address?: string;
  companyLogoBase64?: string;
  signatureBase64?: string;
  currency: string;
  termsAndConditions?: string[];
  showTermsAndConditions?: boolean;
  password?: string;
}

export interface UpdateUserResponse {
  success: boolean;
  message: string;
  id?: number;
  firstName?: string;
  lastName?: string;
  email?: string;
  phoneNumber?: string;
  language?: string;
  profession?: string;
  workType?: string | null;
  companyName?: string;
  gstNumber?: string;
  panNumber?: string;
  address?: string;
  currency?: string;
  companyLogoPath?: string;
  signaturePath?: string;
  termsAndConditions?: string[];
  showTermsAndConditions?: boolean;
}

export interface ChangePasswordRequest {
  userId: number;
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

export interface ChangePasswordResponse {
  success: boolean;
  message: string;
}

export interface DeleteAccountResponse {
  success: boolean;
  message: string;
}

export interface GoogleLoginRequest {
  token: string;
}

export interface FacebookLoginRequest {
  accessToken: string;
}

export interface ForgotPasswordResponse {
  success: boolean;
  message: string;
}

export interface ResetPasswordResponse {
  success: boolean;
  message: string;
}

export interface VerifyEmailRequest {
  email: string;
  code: string;
}

export interface VerifyEmailResponse {
  success: boolean;
  message: string;
}

@Injectable({
  providedIn: 'root'
})
export class RegistrationService {
  private apiUrl = `${environment.apiUrl}/Account`;

  constructor(private http: HttpClient) {}

  register(request: RegisterRequest): Observable<RegisterResponse> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json'
    });

    return this.http.post<RegisterResponse>(`${this.apiUrl}/register`, request, { headers });
  }

  login(request: LoginRequest): Observable<LoginResponse> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json'
    });

    return this.http.post<LoginResponse>(`${this.apiUrl}/login`, request, { headers });
  }

  update(request: UpdateUserRequest): Observable<UpdateUserResponse> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json'
    });

    return this.http.put<UpdateUserResponse>(`${this.apiUrl}/update`, request, { headers });
  }

  changePassword(request: ChangePasswordRequest): Observable<ChangePasswordResponse> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json'
    });

    return this.http.post<ChangePasswordResponse>(`${this.apiUrl}/change-password`, request, { headers });
  }

  deleteAccount(): Observable<DeleteAccountResponse> {
    return this.http.delete<DeleteAccountResponse>(`${this.apiUrl}/delete-account`);
  }

  forgotPassword(request: { email: string }): Observable<ForgotPasswordResponse> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json'
    });

    return this.http.post<ForgotPasswordResponse>(`${this.apiUrl}/forgot-password`, request, { headers });
  }

  resetPassword(request: { email: string; resetToken: string; newPassword: string }): Observable<ResetPasswordResponse> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json'
    });

    return this.http.post<ResetPasswordResponse>(`${this.apiUrl}/reset-password`, request, { headers });
  }

  verifyEmail(request: VerifyEmailRequest): Observable<VerifyEmailResponse> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json'
    });

    return this.http.post<VerifyEmailResponse>(`${this.apiUrl}/verify-email`, request, { headers });
  }

  resendVerificationCode(email: string): Observable<VerifyEmailResponse> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json'
    });

    return this.http.post<VerifyEmailResponse>(`${this.apiUrl}/resend-verification`, { email }, { headers });
  }

  uploadLogo(userId: number, file: File): Observable<string> {
    const formData = new FormData();
    formData.append('file', file);
    
    const headers = new HttpHeaders({
      'userId': userId.toString()
    });

    return this.http.post<string>(`${this.apiUrl}/upload-logo`, formData, { headers, responseType: 'text' as 'json' });
  }

  uploadSignature(userId: number, file: File): Observable<string> {
    const formData = new FormData();
    formData.append('file', file);
    
    const headers = new HttpHeaders({
      'userId': userId.toString()
    });

    return this.http.post<string>(`${this.apiUrl}/upload-signature`, formData, { headers, responseType: 'text' as 'json' });
  }

  refreshToken(refreshToken: string): Observable<RefreshTokenResponse> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json'
    });

    const request: RefreshTokenRequest = { refreshToken };
    return this.http.post<RefreshTokenResponse>(`${this.apiUrl}/refresh-token`, request, { headers });
  }

  getCurrentUser(): Observable<UserProfileResponse> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json'
    });

    return this.http.get<UserProfileResponse>(`${this.apiUrl}/me`, { headers });
  }

  getCompanyLogo(): Observable<Blob> {
    // AuthInterceptor will attach Authorization header
    // Add cache-busting parameter to ensure fresh logo is loaded
    const timestamp = new Date().getTime();
    return this.http.get(`${this.apiUrl}/logo?t=${timestamp}`, { responseType: 'blob' });
  }

  getSignature(): Observable<Blob> {
    // AuthInterceptor will attach Authorization header
    // Add cache-busting parameter to ensure fresh signature is loaded
    const timestamp = new Date().getTime();
    return this.http.get(`${this.apiUrl}/signature?t=${timestamp}`, { responseType: 'blob' });
  }

  /**
   * Convert a File to base64 string
   */
  fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const base64String = reader.result as string;
        // Remove data URL prefix (e.g., "data:image/png;base64,")
        const base64 = base64String.split(',')[1];
        resolve(base64);
      };
      reader.onerror = (error) => reject(error);
    });
  }

  googleLogin(request: GoogleLoginRequest): Observable<LoginResponse> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json'
    });

    return this.http.post<LoginResponse>(`${this.apiUrl}/google-login`, request, { headers });
  }

  facebookLogin(request: FacebookLoginRequest): Observable<LoginResponse> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json'
    });

    return this.http.post<LoginResponse>(`${this.apiUrl}/facebook-login`, request, { headers });
  }
}

